import * as THREE from 'three';

/**
 * Procedural, action-ready reconstruction of the original purple Game Boy Advance.
 *
 * Model space uses +Z as the control face, +Y toward the cartridge edge and -Y
 * toward the headphone edge. Rotate the root -PI / 2 around X to lay it face-up
 * on a conventional Three.js XZ table.
 *
 * The supplied reference is a single front three-quarter photograph. Front-side
 * silhouette, controls and material zoning are evidence-led; rear/underside details
 * and the cartridge well are deliberately marked as functional approximations.
 */

export type GbaControlId =
  | 'dpad-up'
  | 'dpad-down'
  | 'dpad-left'
  | 'dpad-right'
  | 'a'
  | 'b'
  | 'start'
  | 'select'
  | 'power'
  | 'l'
  | 'r';

export type GbaColliderKind = 'box' | 'cylinder' | 'compound-boxes' | 'trigger';

export interface GbaColliderDescriptor {
  id: string;
  kind: GbaColliderKind;
  object: THREE.Object3D;
  size: THREE.Vector3;
  offset: THREE.Vector3;
  radius?: number;
  isTrigger: boolean;
  notes: string;
}

export interface GbaControlHandle {
  id: GbaControlId;
  pivot: THREE.Group;
  hitTargets: THREE.Object3D[];
  restPosition: THREE.Vector3;
  pressedOffset: THREE.Vector3;
  travel: number;
  value: number;
  target: number;
}

export interface GbaCartridgeInterface {
  slot: THREE.Group;
  insertionTrigger: THREE.Mesh;
  alignmentSocket: THREE.Object3D;
  seatedSocket: THREE.Object3D;
  approachDirection: THREE.Vector3;
  entryAngleToleranceRadians: number;
  lateralTolerance: number;
  snapDistance: number;
  insertionTravel: number;
}

export interface GbaSculptRuntime {
  approximateHiddenSurfaces: true;
  referenceLimitations: readonly string[];
  nodes: Record<string, THREE.Object3D>;
  meshes: Record<string, THREE.Mesh>;
  sockets: Record<string, THREE.Object3D>;
  colliders: Record<string, GbaColliderDescriptor>;
  destructionGroups: Record<string, THREE.Object3D[]>;
  controls: Record<GbaControlId, GbaControlHandle>;
  interactiveMeshes: THREE.Object3D[];
  screenMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  screenMaterial: THREE.MeshBasicMaterial;
  cartridge: GbaCartridgeInterface;
  powerState: boolean;
  setScreenTexture(texture: THREE.Texture | null): void;
  setPowerState(on: boolean, immediate?: boolean): void;
  setControlPressed(id: GbaControlId, pressed: boolean, immediate?: boolean): void;
  update(deltaSeconds: number): void;
  dispose(): void;
}

export interface GbaModelOptions {
  /** Dynamic 240x160-style content. CanvasTexture is accepted directly. */
  screenTexture?: THREE.Texture | null;
  poweredOn?: boolean;
  castShadow?: boolean;
  receiveShadow?: boolean;
  /** Global unit multiplier. The unscaled shell is 14.45 units wide. */
  scale?: number;
}

const CONTROL_IDS: readonly GbaControlId[] = [
  'dpad-up',
  'dpad-down',
  'dpad-left',
  'dpad-right',
  'a',
  'b',
  'start',
  'select',
  'power',
  'l',
  'r',
];

const COLORS = {
  purple: 0x4f458e,
  purpleDark: 0x30274f,
  purpleRear: 0x3e3575,
  white: 0xc7c7c4,
  whiteHighlight: 0xe7e7e2,
  bezel: 0x30322f,
  bezelEdge: 0x1c1e1c,
  inactiveLcd: 0x71736d,
  cavity: 0x171323,
  cavityPurple: 0x211b38,
  metal: 0x858983,
  labelWhite: 0xf1f1ec,
  ledOff: 0x253028,
  ledOn: 0x78ff5f,
} as const;

const FULL_TURN = Math.PI * 2;

function roundedRectPath(
  width: number,
  height: number,
  radius: number,
  clockwise = false,
): THREE.Path {
  const path = new THREE.Path();
  const hw = width / 2;
  const hh = height / 2;
  const r = Math.min(radius, hw, hh);

  if (!clockwise) {
    path.moveTo(-hw + r, -hh);
    path.lineTo(hw - r, -hh);
    path.quadraticCurveTo(hw, -hh, hw, -hh + r);
    path.lineTo(hw, hh - r);
    path.quadraticCurveTo(hw, hh, hw - r, hh);
    path.lineTo(-hw + r, hh);
    path.quadraticCurveTo(-hw, hh, -hw, hh - r);
    path.lineTo(-hw, -hh + r);
    path.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
  } else {
    path.moveTo(-hw + r, -hh);
    path.quadraticCurveTo(-hw, -hh, -hw, -hh + r);
    path.lineTo(-hw, hh - r);
    path.quadraticCurveTo(-hw, hh, -hw + r, hh);
    path.lineTo(hw - r, hh);
    path.quadraticCurveTo(hw, hh, hw, hh - r);
    path.lineTo(hw, -hh + r);
    path.quadraticCurveTo(hw, -hh, hw - r, -hh);
    path.lineTo(-hw + r, -hh);
  }

  path.closePath();
  return path;
}

function roundedRectShape(width: number, height: number, radius: number): THREE.Shape {
  const path = roundedRectPath(width, height, radius);
  const shape = new THREE.Shape();
  shape.curves = path.curves;
  shape.currentPoint.copy(path.currentPoint);
  return shape;
}

function roundedFrameShape(
  outerWidth: number,
  outerHeight: number,
  outerRadius: number,
  innerWidth: number,
  innerHeight: number,
  innerRadius: number,
): THREE.Shape {
  const shape = roundedRectShape(outerWidth, outerHeight, outerRadius);
  shape.holes.push(roundedRectPath(innerWidth, innerHeight, innerRadius, true));
  return shape;
}

function roundedRectGeometry(
  width: number,
  height: number,
  radius: number,
  depth: number,
  bevelSize = Math.min(radius * 0.35, depth * 0.35),
  bevelSegments = 3,
): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(roundedRectShape(width, height, radius), {
    depth,
    bevelEnabled: bevelSize > 0,
    bevelThickness: bevelSize,
    bevelSize,
    bevelSegments,
    curveSegments: 12,
    steps: 1,
  });
  geometry.computeVertexNormals();
  return geometry;
}

function roundedFrameGeometry(
  outerWidth: number,
  outerHeight: number,
  outerRadius: number,
  innerWidth: number,
  innerHeight: number,
  innerRadius: number,
  depth: number,
): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(
    roundedFrameShape(
      outerWidth,
      outerHeight,
      outerRadius,
      innerWidth,
      innerHeight,
      innerRadius,
    ),
    {
      depth,
      bevelEnabled: true,
      bevelThickness: 0.055,
      bevelSize: 0.055,
      bevelSegments: 3,
      curveSegments: 16,
      steps: 1,
    },
  );
  geometry.computeVertexNormals();
  return geometry;
}

function wingShape(scale = 1): THREE.Shape {
  const s = scale;
  const shape = new THREE.Shape();

  shape.moveTo(-1.5 * s, 4.02 * s);
  shape.bezierCurveTo(-2.7 * s, 4.18 * s, -3.9 * s, 4.25 * s, -4.8 * s, 3.92 * s);
  shape.bezierCurveTo(-5.6 * s, 3.62 * s, -6.65 * s, 2.68 * s, -7.05 * s, 1.68 * s);
  shape.bezierCurveTo(-7.42 * s, 0.74 * s, -7.36 * s, -1.92 * s, -6.92 * s, -2.92 * s);
  shape.bezierCurveTo(-6.5 * s, -3.88 * s, -5.28 * s, -4.12 * s, -3.82 * s, -4.08 * s);
  shape.bezierCurveTo(-2.45 * s, -4.03 * s, -1.06 * s, -3.92 * s, 0, -3.86 * s);
  shape.bezierCurveTo(1.06 * s, -3.92 * s, 2.45 * s, -4.03 * s, 3.82 * s, -4.08 * s);
  shape.bezierCurveTo(5.28 * s, -4.12 * s, 6.5 * s, -3.88 * s, 6.92 * s, -2.92 * s);
  shape.bezierCurveTo(7.36 * s, -1.92 * s, 7.42 * s, 0.74 * s, 7.05 * s, 1.68 * s);
  shape.bezierCurveTo(6.65 * s, 2.68 * s, 5.6 * s, 3.62 * s, 4.8 * s, 3.92 * s);
  shape.bezierCurveTo(3.9 * s, 4.25 * s, 2.7 * s, 4.18 * s, 1.5 * s, 4.02 * s);
  shape.bezierCurveTo(0.52 * s, 3.87 * s, -0.52 * s, 3.87 * s, -1.5 * s, 4.02 * s);
  shape.closePath();
  return shape;
}

function wingGeometry(
  depth: number,
  scale: number,
  bevelSize: number,
  bevelSegments: number,
): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(wingShape(scale), {
    depth,
    bevelEnabled: true,
    bevelThickness: bevelSize,
    bevelSize,
    bevelSegments,
    curveSegments: 20,
    steps: 1,
  });
  geometry.computeVertexNormals();
  return geometry;
}

function crossShape(armLength: number, armWidth: number, radius: number): THREE.Shape {
  const a = armLength / 2;
  const b = armWidth / 2;
  const points = [
    new THREE.Vector2(-b + radius, -a),
    new THREE.Vector2(b - radius, -a),
    new THREE.Vector2(b, -a + radius),
    new THREE.Vector2(b, -b),
    new THREE.Vector2(a - radius, -b),
    new THREE.Vector2(a, -b + radius),
    new THREE.Vector2(a, b - radius),
    new THREE.Vector2(a - radius, b),
    new THREE.Vector2(b, b),
    new THREE.Vector2(b, a - radius),
    new THREE.Vector2(b - radius, a),
    new THREE.Vector2(-b + radius, a),
    new THREE.Vector2(-b, a - radius),
    new THREE.Vector2(-b, b),
    new THREE.Vector2(-a + radius, b),
    new THREE.Vector2(-a, b - radius),
    new THREE.Vector2(-a, -b + radius),
    new THREE.Vector2(-a + radius, -b),
    new THREE.Vector2(-b, -b),
    new THREE.Vector2(-b, -a + radius),
  ];
  const shape = new THREE.Shape(points);
  shape.closePath();
  return shape;
}

function createSeededRoughnessTexture(seed: number, size = 128): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  let state = seed >>> 0;
  const random = (): number => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0xffffffff;
  };

  for (let index = 0; index < size * size; index += 1) {
    const fine = random() * 0.55 + random() * 0.45;
    const value = Math.round(190 + fine * 52);
    const offset = index * 4;
    data[offset] = value;
    data[offset + 1] = value;
    data[offset + 2] = value;
    data[offset + 3] = 255;
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.name = `gba-roughness-${seed}`;
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(6, 6);
  texture.needsUpdate = true;
  return texture;
}

function createSeededNormalTexture(seed: number, size = 128): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  let state = seed >>> 0;
  const random = (): number => {
    state = (Math.imul(state, 1103515245) + 12345) >>> 0;
    return state / 0xffffffff;
  };

  for (let index = 0; index < size * size; index += 1) {
    const dx = (random() - 0.5) * 0.18;
    const dy = (random() - 0.5) * 0.18;
    const offset = index * 4;
    data[offset] = Math.round((dx + 1) * 127.5);
    data[offset + 1] = Math.round((dy + 1) * 127.5);
    data[offset + 2] = 255;
    data[offset + 3] = 255;
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.name = `gba-normal-${seed}`;
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(8, 8);
  texture.needsUpdate = true;
  return texture;
}

function makeFallbackTexture(color: number): THREE.DataTexture {
  const c = new THREE.Color(color);
  const data = new Uint8Array([
    Math.round(c.r * 255),
    Math.round(c.g * 255),
    Math.round(c.b * 255),
    255,
  ]);
  const texture = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function createInactiveScreenTexture(): THREE.Texture {
  if (typeof document === 'undefined') {
    return makeFallbackTexture(COLORS.inactiveLcd);
  }

  const canvas = document.createElement('canvas');
  canvas.width = 240;
  canvas.height = 160;
  const context = canvas.getContext('2d');
  if (context === null) {
    return makeFallbackTexture(COLORS.inactiveLcd);
  }

  const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#8b8d88');
  gradient.addColorStop(0.48, '#737570');
  gradient.addColorStop(1, '#5d5f5b');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalAlpha = 0.2;
  context.fillStyle = '#242623';
  for (let x = 0; x < canvas.width; x += 2) {
    context.fillRect(x, 0, 1, canvas.height);
  }
  context.globalAlpha = 0.07;
  context.fillStyle = '#ffffff';
  for (let y = 0; y < canvas.height; y += 3) {
    context.fillRect(0, y, canvas.width, 1);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = 'gba-inactive-lcd';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  return texture;
}

function createLabelTexture(
  text: string,
  foreground: string,
  background: string | null,
  width = 512,
  height = 128,
  font = '700 74px Arial, sans-serif',
): THREE.Texture {
  if (typeof document === 'undefined') {
    return makeFallbackTexture(background === null ? 0xffffff : Number.parseInt(background.slice(1), 16));
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) {
    return makeFallbackTexture(COLORS.labelWhite);
  }

  context.clearRect(0, 0, width, height);
  if (background !== null) {
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);
  }
  context.font = font;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillStyle = foreground;
  context.fillText(text, width / 2, height / 2 + height * 0.03, width * 0.94);

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = `gba-label-${text.toLowerCase().replace(/\s+/g, '-')}`;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  return texture;
}

function makeLabelMesh(
  texture: THREE.Texture,
  width: number,
  height: number,
  opacity = 1,
): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
  mesh.renderOrder = 6;
  return mesh;
}

function markVisualMeshes(
  root: THREE.Object3D,
  castShadow: boolean,
  receiveShadow: boolean,
): void {
  root.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = castShadow;
      child.receiveShadow = receiveShadow;
    }
  });
}

function registerNode(
  runtimeNodes: Record<string, THREE.Object3D>,
  id: string,
  object: THREE.Object3D,
): void {
  object.name = `gba:${id}`;
  object.userData.sculptComponentId = id;
  runtimeNodes[id] = object;
}

function createSocket(
  parent: THREE.Object3D,
  runtimeSockets: Record<string, THREE.Object3D>,
  id: string,
  position: THREE.Vector3Tuple,
  rotation: THREE.EulerTuple = [0, 0, 0],
): THREE.Object3D {
  const socket = new THREE.Object3D();
  socket.name = `socket:${id}`;
  socket.position.fromArray(position);
  socket.rotation.set(rotation[0], rotation[1], rotation[2]);
  socket.userData.socketId = id;
  parent.add(socket);
  runtimeSockets[id] = socket;
  return socket;
}

function createControl(
  id: GbaControlId,
  pivot: THREE.Group,
  hitTargets: THREE.Object3D[],
  pressedOffset: THREE.Vector3,
  interactiveMeshes: THREE.Object3D[],
): GbaControlHandle {
  pivot.name = `control:${id}`;
  pivot.userData.controlId = id;
  pivot.userData.interaction = 'physical-control';
  for (const target of hitTargets) {
    target.userData.controlId = id;
    target.userData.interaction = 'physical-control';
    interactiveMeshes.push(target);
  }
  return {
    id,
    pivot,
    hitTargets,
    restPosition: pivot.position.clone(),
    pressedOffset,
    travel: pressedOffset.length(),
    value: 0,
    target: 0,
  };
}

/**
 * Creates a detailed procedural GBA root. The returned Group is the integration
 * surface; its typed runtime API is available at root.userData.sculptRuntime.
 */
export function createGbaModel(options: GbaModelOptions = {}): THREE.Group {
  const root = new THREE.Group();
  root.name = 'gba-root';
  root.scale.setScalar(options.scale ?? 1);

  const nodes: Record<string, THREE.Object3D> = {};
  const meshes: Record<string, THREE.Mesh> = {};
  const sockets: Record<string, THREE.Object3D> = {};
  const colliders: Record<string, GbaColliderDescriptor> = {};
  const interactiveMeshes: THREE.Object3D[] = [];
  const ownedTextures = new Set<THREE.Texture>();
  const ownedMaterials = new Set<THREE.Material>();
  registerNode(nodes, 'root', root);

  const purpleRoughness = createSeededRoughnessTexture(0x6a4da9);
  const purpleNormal = createSeededNormalTexture(0x5173c4);
  const whiteRoughness = createSeededRoughnessTexture(0xc7c7c4);
  const whiteNormal = createSeededNormalTexture(0xf4e9d8);
  const bezelRoughness = createSeededRoughnessTexture(0x30322f);
  const bezelNormal = createSeededNormalTexture(0x1f2320);
  ownedTextures.add(purpleRoughness);
  ownedTextures.add(purpleNormal);
  ownedTextures.add(whiteRoughness);
  ownedTextures.add(whiteNormal);
  ownedTextures.add(bezelRoughness);
  ownedTextures.add(bezelNormal);

  const purpleMaterial = new THREE.MeshPhysicalMaterial({
    name: 'gba-purple-molded-abs',
    color: COLORS.purple,
    roughness: 0.52,
    roughnessMap: purpleRoughness,
    metalness: 0,
    normalMap: purpleNormal,
    normalScale: new THREE.Vector2(0.13, 0.13),
    clearcoat: 0.16,
    clearcoatRoughness: 0.36,
  });
  const purpleRearMaterial = purpleMaterial.clone();
  purpleRearMaterial.name = 'gba-purple-rear-abs';
  purpleRearMaterial.color.setHex(COLORS.purpleRear);
  purpleRearMaterial.roughness = 0.56;
  const whiteMaterial = new THREE.MeshPhysicalMaterial({
    name: 'gba-warm-white-satin-plastic',
    color: COLORS.white,
    roughness: 0.55,
    roughnessMap: whiteRoughness,
    metalness: 0,
    normalMap: whiteNormal,
    normalScale: new THREE.Vector2(0.07, 0.07),
    clearcoat: 0.12,
    clearcoatRoughness: 0.42,
  });
  const bezelMaterial = new THREE.MeshPhysicalMaterial({
    name: 'gba-charcoal-bezel-plastic',
    color: COLORS.bezel,
    roughness: 0.28,
    roughnessMap: bezelRoughness,
    metalness: 0,
    normalMap: bezelNormal,
    normalScale: new THREE.Vector2(0.045, 0.045),
    clearcoat: 0.55,
    clearcoatRoughness: 0.18,
  });
  const bezelEdgeMaterial = new THREE.MeshStandardMaterial({
    name: 'gba-bezel-aperture-shadow',
    color: COLORS.bezelEdge,
    roughness: 0.48,
    metalness: 0,
  });
  const cavityMaterial = new THREE.MeshStandardMaterial({
    name: 'gba-purple-black-cavity',
    color: COLORS.cavityPurple,
    roughness: 0.74,
    metalness: 0,
  });
  const deepCavityMaterial = new THREE.MeshStandardMaterial({
    name: 'gba-deep-cavity',
    color: COLORS.cavity,
    roughness: 0.8,
    metalness: 0,
  });
  const metalMaterial = new THREE.MeshStandardMaterial({
    name: 'gba-contact-metal',
    color: COLORS.metal,
    roughness: 0.34,
    metalness: 0.82,
  });
  const ledMaterial = new THREE.MeshPhysicalMaterial({
    name: 'gba-power-led-lens',
    color: COLORS.ledOff,
    roughness: 0.24,
    metalness: 0,
    clearcoat: 0.65,
    clearcoatRoughness: 0.12,
    emissive: COLORS.ledOn,
    emissiveIntensity: 0,
  });
  for (const material of [
    purpleMaterial,
    purpleRearMaterial,
    whiteMaterial,
    bezelMaterial,
    bezelEdgeMaterial,
    cavityMaterial,
    deepCavityMaterial,
    metalMaterial,
    ledMaterial,
  ]) {
    ownedMaterials.add(material);
  }

  // Rear shell is fractionally larger and darker, leaving a readable molded seam.
  const rearShell = new THREE.Group();
  registerNode(nodes, 'rear-shell', rearShell);
  root.add(rearShell);
  const rearShellMesh = new THREE.Mesh(wingGeometry(0.94, 1.012, 0.19, 4), purpleRearMaterial);
  rearShellMesh.position.z = -1.2;
  rearShell.add(rearShellMesh);
  meshes['rear-shell'] = rearShellMesh;

  const seamMesh = new THREE.Mesh(wingGeometry(0.065, 1.006, 0.04, 2), purpleRearMaterial);
  seamMesh.name = 'gba:front-back-construction-seam';
  seamMesh.position.z = -0.29;
  root.add(seamMesh);
  meshes['front-back-seam'] = seamMesh;

  // White shoulder inserts sit behind the purple face and preserve the reference's two-tone edge.
  const shoulderTrim = new THREE.Group();
  registerNode(nodes, 'shoulder-trim', shoulderTrim);
  root.add(shoulderTrim);
  const trimGeometry = roundedRectGeometry(0.72, 6.24, 0.34, 0.42, 0.08, 3);
  const leftTrim = new THREE.Mesh(trimGeometry, whiteMaterial);
  leftTrim.position.set(-6.77, -0.05, -0.44);
  leftTrim.rotation.z = -0.105;
  const rightTrim = leftTrim.clone();
  rightTrim.position.x = 6.77;
  rightTrim.rotation.z = 0.105;
  shoulderTrim.add(leftTrim, rightTrim);
  meshes['shoulder-trim-left'] = leftTrim;
  meshes['shoulder-trim-right'] = rightTrim;

  const frontShell = new THREE.Group();
  registerNode(nodes, 'shell-front', frontShell);
  root.add(frontShell);
  const frontShellMesh = new THREE.Mesh(wingGeometry(0.98, 1, 0.22, 5), purpleMaterial);
  frontShellMesh.position.z = -0.23;
  frontShell.add(frontShellMesh);
  meshes['shell-front'] = frontShellMesh;

  colliders.shell = {
    id: 'shell',
    kind: 'compound-boxes',
    object: root,
    size: new THREE.Vector3(14.15, 7.72, 2.25),
    offset: new THREE.Vector3(0, -0.02, -0.08),
    isTrigger: false,
    notes: 'Center box plus left/right wing proxies; visual shell is never used as a collider.',
  };

  // Screen stack: solid bezel carrier, inset aperture shadow, live screen plane and wordmark.
  const screenAssembly = new THREE.Group();
  registerNode(nodes, 'screen-assembly', screenAssembly);
  screenAssembly.position.set(0, 0.52, 0.81);
  root.add(screenAssembly);

  const bezelMesh = new THREE.Mesh(
    roundedFrameGeometry(8.25, 5.72, 0.55, 6.87, 4.58, 0.18, 0.2),
    bezelMaterial,
  );
  registerNode(nodes, 'screen-bezel', bezelMesh);
  screenAssembly.add(bezelMesh);
  meshes['screen-bezel'] = bezelMesh;

  const apertureShadow = new THREE.Mesh(
    roundedRectGeometry(6.95, 4.66, 0.2, 0.08, 0.015, 1),
    bezelEdgeMaterial,
  );
  apertureShadow.name = 'gba:lcd-aperture-shadow';
  apertureShadow.position.set(0, 0.25, 0.13);
  screenAssembly.add(apertureShadow);
  meshes['lcd-aperture-shadow'] = apertureShadow;

  const inactiveScreenTexture = createInactiveScreenTexture();
  ownedTextures.add(inactiveScreenTexture);
  const screenMaterial = new THREE.MeshBasicMaterial({
    name: 'gba-dynamic-lcd-material',
    map: options.screenTexture ?? inactiveScreenTexture,
    color: 0x6b6e68,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  ownedMaterials.add(screenMaterial);
  // Keep the active LCD on a standard 0..1 UV plane so a caller-provided
  // CanvasTexture fills the display exactly. The rounded aperture above masks
  // the four square plane corners, preserving the molded screen silhouette.
  const screenGeometry = new THREE.PlaneGeometry(6.7, 4.4);
  const screenMesh = new THREE.Mesh(screenGeometry, screenMaterial);
  registerNode(nodes, 'lcd-display', screenMesh);
  // Keep a visible depth gap from the aperture backing. At the shallow display
  // camera angle, the former 0.015-unit separation produced LCD-wide z-fighting
  // bands that obscured the pixel output.
  screenMesh.position.set(0, 0.25, 0.285);
  screenMesh.renderOrder = 4;
  screenAssembly.add(screenMesh);
  meshes['lcd-display'] = screenMesh;

  const screenSocket = createSocket(
    screenAssembly,
    sockets,
    'screen-content-socket',
    [0, 0.25, 0.295],
  );
  screenSocket.userData.accepts = 'THREE.CanvasTexture';

  const gbaWordmarkTexture = createLabelTexture(
    'GAME BOY ADVANCE',
    '#F1F1EC',
    null,
    1024,
    180,
    '900 108px Arial Narrow, Arial, sans-serif',
  );
  ownedTextures.add(gbaWordmarkTexture);
  const gbaWordmark = makeLabelMesh(gbaWordmarkTexture, 4.15, 0.48);
  gbaWordmark.name = 'gba:game-boy-advance-wordmark';
  gbaWordmark.position.set(0, -2.47, 0.315);
  screenAssembly.add(gbaWordmark);
  meshes['game-boy-advance-wordmark'] = gbaWordmark;

  colliders.screen = {
    id: 'screen',
    kind: 'box',
    object: screenAssembly,
    size: new THREE.Vector3(8.25, 5.72, 0.42),
    offset: new THREE.Vector3(0, 0, 0.1),
    isTrigger: false,
    notes: 'Rigid screen-and-bezel module.',
  };

  // Molded shell labels are subtle purple-on-purple relief/decal carriers.
  const nintendoTexture = createLabelTexture(
    'Nintendo',
    '#40366A',
    null,
    512,
    150,
    'italic 800 82px Arial, sans-serif',
  );
  ownedTextures.add(nintendoTexture);
  const nintendoLabel = makeLabelMesh(nintendoTexture, 2.25, 0.66, 0.56);
  nintendoLabel.name = 'gba:nintendo-emboss';
  nintendoLabel.position.set(1.72, 3.13, 0.82);
  root.add(nintendoLabel);

  const powerLabelTexture = createLabelTexture('POWER', '#40366A', null, 380, 100, '700 54px Arial');
  ownedTextures.add(powerLabelTexture);
  const powerLabel = makeLabelMesh(powerLabelTexture, 1.18, 0.34, 0.48);
  powerLabel.name = 'gba:power-label';
  powerLabel.position.set(5.45, 2.16, 0.835);
  root.add(powerLabel);

  const controls = {} as Record<GbaControlId, GbaControlHandle>;

  // Cross recess and four separately raycastable D-pad directions.
  const dpadRecessGeometry = new THREE.ExtrudeGeometry(crossShape(2.02, 0.72, 0.07), {
    depth: 0.08,
    bevelEnabled: true,
    bevelThickness: 0.035,
    bevelSize: 0.035,
    bevelSegments: 2,
    curveSegments: 8,
  });
  const dpadRecess = new THREE.Mesh(dpadRecessGeometry, cavityMaterial);
  registerNode(nodes, 'dpad-recess', dpadRecess);
  dpadRecess.position.set(-5.95, 1.5, 0.77);
  root.add(dpadRecess);
  meshes['dpad-recess'] = dpadRecess;

  const dpadGroup = new THREE.Group();
  registerNode(nodes, 'dpad', dpadGroup);
  dpadGroup.position.set(-5.95, 1.5, 0.92);
  root.add(dpadGroup);
  const dpadPieceGeometry = roundedRectGeometry(0.65, 1.05, 0.12, 0.17, 0.045, 2);
  const dpadDirections: readonly [GbaControlId, THREE.Vector3Tuple, number, THREE.Vector3Tuple][] = [
    ['dpad-up', [0, 0.49, 0], 0, [0, 0, -0.075]],
    ['dpad-right', [0.49, 0, 0], -Math.PI / 2, [0, 0, -0.075]],
    ['dpad-down', [0, -0.49, 0], Math.PI, [0, 0, -0.075]],
    ['dpad-left', [-0.49, 0, 0], Math.PI / 2, [0, 0, -0.075]],
  ];
  for (const [id, position, rotationZ, pressOffset] of dpadDirections) {
    const pivot = new THREE.Group();
    pivot.position.fromArray(position);
    pivot.rotation.z = rotationZ;
    const mesh = new THREE.Mesh(dpadPieceGeometry, whiteMaterial);
    mesh.position.z = 0;
    pivot.add(mesh);
    dpadGroup.add(pivot);
    controls[id] = createControl(id, pivot, [mesh], new THREE.Vector3(...pressOffset), interactiveMeshes);
    meshes[id] = mesh;

    const triangle = new THREE.Mesh(
      new THREE.ShapeGeometry(
        new THREE.Shape([
          new THREE.Vector2(0, 0.11),
          new THREE.Vector2(-0.1, -0.08),
          new THREE.Vector2(0.1, -0.08),
        ]),
      ),
      new THREE.MeshBasicMaterial({ color: 0xaaa9a6, transparent: true, opacity: 0.58, toneMapped: false }),
    );
    ownedMaterials.add(triangle.material);
    triangle.name = `gba:${id}-direction-mark`;
    triangle.position.set(0, 0.24, 0.245);
    pivot.add(triangle);
  }
  const dpadCenter = new THREE.Mesh(
    new THREE.CylinderGeometry(0.28, 0.28, 0.18, 28),
    whiteMaterial,
  );
  dpadCenter.rotation.x = Math.PI / 2;
  dpadCenter.position.z = 0.08;
  dpadCenter.name = 'gba:dpad-center-disc';
  dpadGroup.add(dpadCenter);
  meshes['dpad-center'] = dpadCenter;

  // Face buttons are true plunger groups with separate meshes and glyphs.
  const faceButtonGroup = new THREE.Group();
  registerNode(nodes, 'face-buttons', faceButtonGroup);
  faceButtonGroup.position.set(5.82, -0.02, 0.95);
  faceButtonGroup.rotation.z = -0.11;
  root.add(faceButtonGroup);
  const faceButtonGeometry = new THREE.CylinderGeometry(0.49, 0.52, 0.29, 40, 2);
  const makeFaceButton = (
    id: 'a' | 'b',
    position: THREE.Vector3Tuple,
    glyph: string,
  ): void => {
    const pivot = new THREE.Group();
    pivot.position.fromArray(position);
    const mesh = new THREE.Mesh(faceButtonGeometry, whiteMaterial);
    mesh.rotation.x = Math.PI / 2;
    mesh.position.z = 0.1;
    pivot.add(mesh);
    faceButtonGroup.add(pivot);
    controls[id] = createControl(id, pivot, [mesh], new THREE.Vector3(0, 0, -0.1), interactiveMeshes);
    meshes[`button-${id}`] = mesh;
    const texture = createLabelTexture(glyph, '#AAA9A6', null, 128, 128, '700 86px Arial');
    ownedTextures.add(texture);
    const label = makeLabelMesh(texture, 0.43, 0.43, 0.62);
    label.name = `gba:button-${id}-glyph`;
    label.position.z = 0.26;
    pivot.add(label);
  };
  makeFaceButton('b', [-0.77, -0.12, 0], 'B');
  makeFaceButton('a', [0.77, 0.12, 0], 'A');

  // Start and Select: small round gray plungers matching the original hardware.
  const menuButtonGeometry = new THREE.CylinderGeometry(0.21, 0.23, 0.16, 28);
  const menuButtonMaterial = whiteMaterial.clone();
  menuButtonMaterial.name = 'gba-menu-button-gray';
  menuButtonMaterial.color.setHex(0x999a98);
  ownedMaterials.add(menuButtonMaterial);
  const makeMenuButton = (
    id: 'start' | 'select',
    position: THREE.Vector3Tuple,
    labelText: string,
  ): void => {
    const pivot = new THREE.Group();
    pivot.position.fromArray(position);
    const mesh = new THREE.Mesh(menuButtonGeometry, menuButtonMaterial);
    mesh.rotation.x = Math.PI / 2;
    pivot.add(mesh);
    root.add(pivot);
    controls[id] = createControl(id, pivot, [mesh], new THREE.Vector3(0, 0, -0.075), interactiveMeshes);
    meshes[`button-${id}`] = mesh;

    const labelTexture = createLabelTexture(labelText, '#40366A', null, 420, 100, '700 54px Arial');
    ownedTextures.add(labelTexture);
    const label = makeLabelMesh(labelTexture, 1.45, 0.34, 0.48);
    label.name = `gba:${id}-molded-label`;
    label.position.set(position[0] - 0.42, position[1] + 0.04, 0.83);
    label.rotation.z = -0.34;
    root.add(label);
  };
  makeMenuButton('start', [-5.18, -0.66, 0.94], 'START');
  makeMenuButton('select', [-5.49, -1.34, 0.94], 'SELECT');

  // Power LED and the physically sliding bottom-left power switch.
  const ledGroup = new THREE.Group();
  registerNode(nodes, 'power-led', ledGroup);
  ledGroup.position.set(4.72, 2.24, 0.91);
  root.add(ledGroup);
  const ledMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.11, 32), ledMaterial);
  ledMesh.rotation.x = Math.PI / 2;
  ledGroup.add(ledMesh);
  meshes['power-led'] = ledMesh;
  createSocket(ledGroup, sockets, 'power-light-socket', [0, 0, 0.08]);

  const powerChannel = new THREE.Mesh(
    roundedRectGeometry(1.04, 0.44, 0.15, 0.09, 0.02, 2),
    deepCavityMaterial,
  );
  powerChannel.name = 'gba:power-switch-channel';
  // Mount the switch on the visible front-left edge. Keeping it flat on the
  // face placed it below the raised shell and made only its hit target visible.
  powerChannel.position.set(-5.12, -4.08, -0.06);
  powerChannel.rotation.set(Math.PI / 2, 0, -0.22);
  root.add(powerChannel);

  const powerPivot = new THREE.Group();
  powerPivot.position.set(-5.3, -4.12, -0.02);
  powerPivot.rotation.copy(powerChannel.rotation);
  const powerSwitchMesh = new THREE.Mesh(
    roundedRectGeometry(0.6, 0.42, 0.12, 0.12, 0.035, 2),
    whiteMaterial,
  );
  powerPivot.add(powerSwitchMesh);
  root.add(powerPivot);
  controls.power = createControl(
    'power',
    powerPivot,
    [powerSwitchMesh],
    new THREE.Vector3(0.32, 0, 0),
    interactiveMeshes,
  );
  meshes['power-switch'] = powerSwitchMesh;
  for (let index = -2; index <= 2; index += 1) {
    const ridge = new THREE.Mesh(
      new THREE.BoxGeometry(0.32, 0.018, 0.03),
      cavityMaterial,
    );
    ridge.position.set(0, index * 0.055, 0.25);
    powerPivot.add(ridge);
  }

  // Original shoulder buttons/caps complete the rear-edge silhouette.
  const shoulderGeometry = roundedRectGeometry(2.25, 0.42, 0.18, 0.32, 0.06, 3);
  const makeShoulderButton = (id: 'l' | 'r', x: number): void => {
    const pivot = new THREE.Group();
    pivot.position.set(x, 3.88, -0.64);
    const mesh = new THREE.Mesh(shoulderGeometry, whiteMaterial);
    pivot.add(mesh);
    root.add(pivot);
    controls[id] = createControl(id, pivot, [mesh], new THREE.Vector3(0, -0.08, 0), interactiveMeshes);
    meshes[`shoulder-${id}`] = mesh;
  };
  makeShoulderButton('l', -4.72);
  makeShoulderButton('r', 4.72);

  // Six real recessed speaker slots, with the slight length taper visible in the photo.
  const speakerGroup = new THREE.Group();
  registerNode(nodes, 'speaker-bank', speakerGroup);
  speakerGroup.position.set(4.45, -2.63, 0.94);
  speakerGroup.rotation.z = -0.045;
  root.add(speakerGroup);
  for (let index = 0; index < 6; index += 1) {
    const width = 1.72 - index * 0.055;
    const slot = new THREE.Mesh(
      roundedRectGeometry(width, 0.105, 0.052, 0.045, 0.012, 2),
      cavityMaterial,
    );
    slot.name = `gba:speaker-slot-${index + 1}`;
    slot.position.set(index * 0.015, (index - 2.5) * 0.235, 0);
    speakerGroup.add(slot);
    meshes[`speaker-slot-${index + 1}`] = slot;
  }

  // Headphone jack, volume wheel and underside labels.
  const bottomPorts = new THREE.Group();
  registerNode(nodes, 'bottom-ports', bottomPorts);
  root.add(bottomPorts);
  const headphoneRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.28, 0.07, 12, 36),
    cavityMaterial,
  );
  headphoneRing.name = 'gba:headphone-jack-ring';
  headphoneRing.rotation.x = Math.PI / 2;
  headphoneRing.position.set(0.02, -4.1, -0.34);
  bottomPorts.add(headphoneRing);
  const headphoneInterior = new THREE.Mesh(
    new THREE.CylinderGeometry(0.19, 0.19, 0.36, 28),
    deepCavityMaterial,
  );
  headphoneInterior.name = 'gba:headphone-jack-cavity';
  headphoneInterior.position.set(0.02, -4.15, -0.34);
  bottomPorts.add(headphoneInterior);
  meshes['headphone-jack'] = headphoneInterior;

  const volumeWheel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.25, 0.25, 0.58, 24),
    whiteMaterial,
  );
  volumeWheel.name = 'gba:volume-wheel';
  volumeWheel.position.set(1.72, -4.08, -0.33);
  volumeWheel.rotation.z = Math.PI / 2;
  bottomPorts.add(volumeWheel);
  meshes['volume-wheel'] = volumeWheel;
  for (let index = 0; index < 12; index += 1) {
    const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.12, 0.035), cavityMaterial);
    const angle = (index / 12) * FULL_TURN;
    tooth.position.set(
      1.72 + Math.cos(angle) * 0.25,
      -4.08,
      -0.33 + Math.sin(angle) * 0.25,
    );
    tooth.rotation.y = -angle;
    bottomPorts.add(tooth);
  }

  const phonesTexture = createLabelTexture('PHONES', '#40366A', null, 360, 90, '700 52px Arial');
  const volumeTexture = createLabelTexture('VOLUME', '#40366A', null, 420, 90, '700 52px Arial');
  ownedTextures.add(phonesTexture);
  ownedTextures.add(volumeTexture);
  const phonesLabel = makeLabelMesh(phonesTexture, 1.12, 0.28, 0.5);
  phonesLabel.position.set(0.02, -3.73, 0.76);
  phonesLabel.name = 'gba:phones-label';
  root.add(phonesLabel);
  const volumeLabel = makeLabelMesh(volumeTexture, 1.28, 0.28, 0.5);
  volumeLabel.position.set(1.72, -3.73, 0.76);
  volumeLabel.name = 'gba:volume-label';
  root.add(volumeLabel);

  // Rear functional cartridge cavity. The cavity and rails are real meshes, not a painted slot.
  const cartridgeSlot = new THREE.Group();
  registerNode(nodes, 'cartridge-slot', cartridgeSlot);
  cartridgeSlot.position.set(0, 3.62, -0.83);
  root.add(cartridgeSlot);
  const slotCavity = new THREE.Mesh(
    roundedRectGeometry(6.25, 0.72, 0.12, 0.5, 0.035, 2),
    deepCavityMaterial,
  );
  slotCavity.name = 'gba:cartridge-slot-cavity';
  slotCavity.position.set(0, 0, -0.13);
  cartridgeSlot.add(slotCavity);
  meshes['cartridge-slot-cavity'] = slotCavity;

  const slotLipGeometry = roundedRectGeometry(6.52, 0.17, 0.07, 0.29, 0.025, 2);
  const slotFrontLip = new THREE.Mesh(slotLipGeometry, purpleRearMaterial);
  slotFrontLip.position.set(0, -0.34, 0.11);
  slotFrontLip.name = 'gba:cartridge-slot-front-lip';
  const slotRearLip = slotFrontLip.clone();
  slotRearLip.position.y = 0.34;
  slotRearLip.name = 'gba:cartridge-slot-rear-lip';
  cartridgeSlot.add(slotFrontLip, slotRearLip);

  const guideGeometry = new THREE.BoxGeometry(0.16, 0.88, 0.34);
  const leftGuide = new THREE.Mesh(guideGeometry, cavityMaterial);
  leftGuide.position.set(-2.98, 0, 0.08);
  leftGuide.name = 'gba:cartridge-guide-left';
  const rightGuide = leftGuide.clone();
  rightGuide.position.x = 2.98;
  rightGuide.name = 'gba:cartridge-guide-right';
  cartridgeSlot.add(leftGuide, rightGuide);

  const contactStrip = new THREE.Mesh(
    new THREE.BoxGeometry(4.82, 0.07, 0.08),
    metalMaterial,
  );
  contactStrip.name = 'gba:cartridge-contact-strip';
  contactStrip.position.set(0, -0.16, -0.03);
  cartridgeSlot.add(contactStrip);

  const alignmentSocket = createSocket(
    cartridgeSlot,
    sockets,
    'cartridge-align-socket',
    [0, 1.22, 0.16],
  );
  const seatedSocket = createSocket(
    cartridgeSlot,
    sockets,
    'cartridge-seated-socket',
    [0, -0.02, 0.16],
  );
  alignmentSocket.userData.insertionAxis = [0, -1, 0];
  seatedSocket.userData.state = 'inserted';

  const insertionTriggerMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ff88,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  ownedMaterials.add(insertionTriggerMaterial);
  const insertionTrigger = new THREE.Mesh(
    new THREE.BoxGeometry(6.75, 2.1, 1.2),
    insertionTriggerMaterial,
  );
  insertionTrigger.name = 'collider:cartridge-insertion-trigger';
  insertionTrigger.position.set(0, 0.83, 0.1);
  insertionTrigger.userData.interaction = 'cartridge-insertion-zone';
  insertionTrigger.userData.colliderId = 'cartridge-insertion-trigger';
  cartridgeSlot.add(insertionTrigger);
  interactiveMeshes.push(insertionTrigger);

  colliders['cartridge-insertion-trigger'] = {
    id: 'cartridge-insertion-trigger',
    kind: 'trigger',
    object: insertionTrigger,
    size: new THREE.Vector3(6.75, 2.1, 1.2),
    offset: new THREE.Vector3(0, 0.83, 0.1),
    isTrigger: true,
    notes: 'Alignment-assist region; successful insertion still requires valid orientation and release.',
  };

  // Inferred rear battery panel and instanced fasteners retain hidden-side credibility.
  const batteryDoor = new THREE.Mesh(
    roundedRectGeometry(6.05, 3.18, 0.34, 0.11, 0.035, 2),
    purpleRearMaterial,
  );
  registerNode(nodes, 'battery-door', batteryDoor);
  batteryDoor.position.set(0, -0.48, -1.27);
  batteryDoor.rotation.y = Math.PI;
  root.add(batteryDoor);
  meshes['battery-door'] = batteryDoor;

  const screwGeometry = new THREE.CylinderGeometry(0.08, 0.08, 0.035, 14);
  const screwInstances = new THREE.InstancedMesh(screwGeometry, metalMaterial, 6);
  screwInstances.name = 'gba:rear-fasteners';
  const screwPositions: readonly THREE.Vector3Tuple[] = [
    [-5.7, 2.65, -1.28],
    [5.7, 2.65, -1.28],
    [-5.45, -2.82, -1.28],
    [5.45, -2.82, -1.28],
    [-3.32, 0.65, -1.28],
    [3.32, 0.65, -1.28],
  ];
  const dummy = new THREE.Object3D();
  screwPositions.forEach((position, index) => {
    dummy.position.fromArray(position);
    dummy.rotation.set(Math.PI / 2, 0, 0);
    dummy.updateMatrix();
    screwInstances.setMatrixAt(index, dummy.matrix);
  });
  screwInstances.instanceMatrix.needsUpdate = true;
  root.add(screwInstances);
  nodes['rear-fasteners'] = screwInstances;

  // Control collider metadata remains engine-agnostic while hit meshes stay raycastable.
  for (const id of CONTROL_IDS) {
    const control = controls[id];
    const cylinder = id === 'a' || id === 'b';
    colliders[`control-${id}`] = {
      id: `control-${id}`,
      kind: cylinder ? 'cylinder' : 'box',
      object: control.pivot,
      size: cylinder ? new THREE.Vector3(1.22, 1.22, 0.4) : new THREE.Vector3(0.9, 1.15, 0.42),
      offset: new THREE.Vector3(),
      radius: cylinder ? 0.61 : undefined,
      isTrigger: true,
      notes: 'Interactive trigger; visible control mesh supplies raycast target.',
    };
  }

  createSocket(root, sockets, 'console-grip-left', [-6.2, -0.45, -0.15]);
  createSocket(root, sockets, 'console-grip-right', [6.2, -0.45, -0.15]);
  createSocket(root, sockets, 'speaker-emitter', [4.45, -2.63, 0.92]);

  const cartridge: GbaCartridgeInterface = {
    slot: cartridgeSlot,
    insertionTrigger,
    alignmentSocket,
    seatedSocket,
    approachDirection: new THREE.Vector3(0, -1, 0),
    entryAngleToleranceRadians: THREE.MathUtils.degToRad(22),
    lateralTolerance: 0.42,
    snapDistance: 0.52,
    insertionTravel: 1.24,
  };

  let powerState = options.poweredOn ?? false;
  let currentScreenTexture: THREE.Texture = options.screenTexture ?? inactiveScreenTexture;

  const updateControl = (control: GbaControlHandle, deltaSeconds: number): void => {
    const smoothing = 1 - Math.exp(-Math.max(0, deltaSeconds) * 24);
    control.value = THREE.MathUtils.lerp(control.value, control.target, smoothing);
    control.pivot.position.copy(control.restPosition).addScaledVector(control.pressedOffset, control.value);
  };

  const runtime: GbaSculptRuntime = {
    approximateHiddenSurfaces: true,
    referenceLimitations: [
      'Rear shell, battery door, shoulder controls and cartridge rails are inferred from one front three-quarter image.',
      'Dimensions are real-time visual proportions, not manufacturing measurements.',
      'Procedural labels match placement and hierarchy; exact production typography is approximate.',
    ],
    nodes,
    meshes,
    sockets,
    colliders,
    destructionGroups: {
      'console-assembly': [root],
      'front-shell': [frontShell, screenAssembly, dpadGroup, faceButtonGroup],
      'rear-shell': [rearShell, batteryDoor, cartridgeSlot],
      controls: CONTROL_IDS.map((id) => controls[id].pivot),
      electronics: [screenMesh, ledGroup],
    },
    controls,
    interactiveMeshes,
    screenMesh,
    screenMaterial,
    cartridge,
    powerState,
    setScreenTexture(texture: THREE.Texture | null): void {
      currentScreenTexture = texture ?? inactiveScreenTexture;
      currentScreenTexture.colorSpace = THREE.SRGBColorSpace;
      currentScreenTexture.magFilter = THREE.NearestFilter;
      currentScreenTexture.minFilter = THREE.NearestFilter;
      currentScreenTexture.generateMipmaps = false;
      currentScreenTexture.needsUpdate = true;
      screenMaterial.map = currentScreenTexture;
      screenMaterial.needsUpdate = true;
    },
    setPowerState(on: boolean, immediate = false): void {
      powerState = on;
      runtime.powerState = on;
      controls.power.target = on ? 1 : 0;
      ledMaterial.emissiveIntensity = on ? 2.35 : 0;
      ledMaterial.color.setHex(on ? 0x49634a : COLORS.ledOff);
      screenMaterial.color.setHex(on ? 0xffffff : 0x6b6e68);
      if (immediate) {
        controls.power.value = controls.power.target;
        controls.power.pivot.position
          .copy(controls.power.restPosition)
          .addScaledVector(controls.power.pressedOffset, controls.power.value);
      }
    },
    setControlPressed(id: GbaControlId, pressed: boolean, immediate = false): void {
      const control = controls[id];
      control.target = pressed ? 1 : 0;
      if (immediate) {
        control.value = control.target;
        control.pivot.position
          .copy(control.restPosition)
          .addScaledVector(control.pressedOffset, control.value);
      }
    },
    update(deltaSeconds: number): void {
      for (const id of CONTROL_IDS) {
        if (id === 'power') {
          controls.power.target = powerState ? 1 : 0;
        }
        updateControl(controls[id], deltaSeconds);
      }
    },
    dispose(): void {
      root.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) {
          child.geometry.dispose();
        }
      });
      for (const material of ownedMaterials) {
        material.dispose();
      }
      for (const texture of ownedTextures) {
        texture.dispose();
      }
    },
  };

  runtime.setScreenTexture(currentScreenTexture);
  runtime.setPowerState(powerState, true);
  root.userData.sculptRuntime = runtime;
  root.userData.referenceFidelity = {
    target: 0.82,
    source: `${import.meta.env.BASE_URL}references/gba.png`,
    frontConfidence: 0.93,
    hiddenSurfaceConfidence: 0.55,
    statement: 'Strong real-time procedural front-side match; hidden sides remain approximate from one image.',
  };
  root.userData.recommendedTableRotationX = -Math.PI / 2;
  root.userData.modelDimensions = new THREE.Vector3(14.45, 8.2, 2.45);

  markVisualMeshes(root, options.castShadow ?? true, options.receiveShadow ?? true);
  insertionTrigger.castShadow = false;
  insertionTrigger.receiveShadow = false;
  screenMesh.castShadow = false;
  // Tiny instanced rear screws are below the tabletop in the presentation pose;
  // excluding them from the shadow pass also avoids an instanced-depth shader
  // validation fault in headless/SwiftShader browsers.
  screwInstances.castShadow = false;
  gbaWordmark.castShadow = false;
  nintendoLabel.castShadow = false;
  powerLabel.castShadow = false;
  phonesLabel.castShadow = false;
  volumeLabel.castShadow = false;

  return root;
}

export function getGbaRuntime(root: THREE.Object3D): GbaSculptRuntime {
  const runtime = root.userData.sculptRuntime as GbaSculptRuntime | undefined;
  if (runtime === undefined) {
    throw new Error('The supplied object is not a Game Boy Advance model root.');
  }
  return runtime;
}
